<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { apiGet } from "../api";
import Pager from "../components/Pager.vue";
import SortTh from "../components/SortTh.vue";

interface Money { amount: number }
interface OrderRow { id: string; orderNumber: string; status: string; customerName?: string; customerEmail: string; total: Money; itemCount: number; createdAt: string }

const STATUS: Record<string, { label: string; cls: string }> = {
  pending_payment: { label: "Te betalen", cls: "blue" },
  paid: { label: "Betaald", cls: "green" },
  nieuw: { label: "Nieuw", cls: "blue" },
  in_behandeling: { label: "In behandeling", cls: "amber" },
  verzonden: { label: "Verzonden", cls: "green" },
  retour: { label: "Retour", cls: "red" },
};
const STATUS_KEYS = ["pending_payment", "paid", "in_behandeling", "verzonden", "retour"];
const euro = (c: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
const datum = (s: string) =>
  new Date(s).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const router = useRouter();
const rows = ref<OrderRow[]>([]);
const loading = ref(true);
const error = ref("");
const fStatus = ref("alle");
const sort = ref("date");
const order = ref<"asc" | "desc">("desc");
const limit = 50;
const offset = ref(0);
const total = ref(0);

async function load() {
  loading.value = true;
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset.value), sort: sort.value, order: order.value });
  if (fStatus.value !== "alle") qs.set("status", fStatus.value);
  try {
    const d = await apiGet<{ items: OrderRow[]; total: number }>(`/orders?${qs.toString()}`, { auth: true });
    rows.value = d.items; total.value = d.total;
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
function reload() { if (offset.value !== 0) offset.value = 0; else load(); }
function setSort(col: string) {
  if (sort.value === col) order.value = order.value === "asc" ? "desc" : "asc";
  else { sort.value = col; order.value = "asc"; }
}
watch([fStatus, sort, order], reload);
watch(offset, load);
onMounted(load);
</script>

<template>
  <div>
    <h1 class="title">Bestellingen <span class="count">· {{ total }}</span></h1>

    <div class="frow">
      <button class="chip" :class="{ on: fStatus === 'alle' }" @click="fStatus = 'alle'">Alle</button>
      <button v-for="s in STATUS_KEYS" :key="s" class="chip" :class="{ on: fStatus === s }" @click="fStatus = s">
        {{ STATUS[s]?.label || s }}
      </button>
    </div>

    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <template v-else>
      <table class="tbl">
        <thead><tr>
          <SortTh label="Bestelling" col="orderNumber" :sort="sort" :order="order" @sort="setSort" />
          <SortTh label="Klant" col="customer" :sort="sort" :order="order" @sort="setSort" />
          <SortTh label="Datum" col="date" :sort="sort" :order="order" @sort="setSort" />
          <th>Items</th>
          <SortTh label="Totaal" col="total" :sort="sort" :order="order" @sort="setSort" />
          <SortTh label="Status" col="status" :sort="sort" :order="order" @sort="setSort" />
        </tr></thead>
        <tbody>
          <tr v-for="o in rows" :key="o.id" class="clickrow" @click="router.push(`/bestellingen/${o.id}`)">
            <td class="mono">{{ o.orderNumber }}</td>
            <td>{{ o.customerName || o.customerEmail }}</td>
            <td>{{ datum(o.createdAt) }}</td>
            <td>{{ o.itemCount }}</td>
            <td>{{ euro(o.total.amount) }}</td>
            <td><span class="badge" :class="STATUS[o.status]?.cls">{{ STATUS[o.status]?.label || o.status }}</span></td>
          </tr>
          <tr v-if="!rows.length"><td colspan="6" class="empty">Geen bestellingen.</td></tr>
        </tbody>
      </table>
      <Pager v-model:offset="offset" :limit="limit" :total="total" />
    </template>
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 16px; }
.count { color: var(--muted); font-weight: 600; }
.muted { color: var(--muted); } .err { color: var(--red); }
.frow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.chip { border: 1px solid var(--border-input); background: var(--surface); border-radius: 999px; padding: 6px 13px; font-size: 13px; font-weight: 600; cursor: pointer; }
.chip.on { background: var(--ink); color: #fff; border-color: var(--ink); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 14px 12px; border-bottom: 1px solid var(--hairline); }
.empty { text-align: center; color: var(--muted); padding: 28px 12px; }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.mono { font-family: monospace; }
.badge { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 999px; }
.badge.blue { color: #1d4ed8; background: #e5edfb; }
.badge.green { color: var(--green); background: var(--green-bg); }
.badge.amber { color: var(--amber); background: var(--amber-bg); }
.badge.red { color: var(--red); background: var(--red-bg); }
</style>
